import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import AntIcon, { type AntIconType } from "@/components/icons/AntIcon";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { useScrollToTopOnLoad } from "@/utils/hooksUtils";
import { isDeepEqual, isDefined } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { FieldData } from "@rc-component/form/es/interface";
import { App as AntApp, Card, Col, Form, Radio, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useValidateClient } from "../../../../../../common/api/queries";
import { rowGutter } from "../../../../../../common/constants";
import type { FieldConstraintViolation } from "../../../../../../common/types";
import { CalcValidationGroup, ClientFormType, ClientType } from "../../../../../client/enums";
import {
  Client,
  CreateUpdateContractClient,
  LegalClient,
  NaturalClient,
  SelfEmployedClient,
} from "../../../../../client/types";
import {
  clientToCreateUpdateContractClient,
  createUpdateContractClientToClient,
  processClientsDataViolations,
  resolveClientIdentifier,
} from "../../../../../client/utils";
import { DashboardNoticePosition } from "../../../../../dashboard/enums";
import { CalcType } from "../../../../enums";
import { CALC_ROUTE_PATHS } from "../../../../paths";
import { CalcNavigation } from "../../../components/CalcNavigation";
import { CalcNoticesIconView } from "../../../components/CalcNoticesIconView";
import { BuildingType, NearbyBuildingType } from "../../enums";
import { RealtyCalc, RealtyFormClients } from "../../types";
import { realtyCalcPageTitle } from "../../utils";
import { RealtyCalcApartmentSection } from "./sections/RealtyCalcApartmentSection";
import { RealtyCalcGeneralSection } from "./sections/RealtyCalcGeneralSection";
import { RealtyCalcHouseholdRealtySection } from "./sections/RealtyCalcHouseholdRealtySection";
import { RealtyCalcHouseSection } from "./sections/RealtyCalcHouseSection";
import { RealtyCalcInsuranceSection } from "./sections/RealtyCalcInsuranceSection";

export const POLICY_HOLDER_INDEX_MAP = new Map<ClientFormType, number>([[ClientFormType.POLICY_HOLDER, 0]]);

const resolveBuildingTypeIcon = (buildingType: BuildingType): AntIconType => {
  switch (buildingType) {
    case BuildingType.HOUSE:
      return "home";
    case BuildingType.APARTMENT:
      return "hdd";
    case BuildingType.RECREATIONAL_BUILDING:
      return "shop";
  }
};

interface Props {
  calcData?: RealtyCalc;
  formErrors: FieldConstraintViolation[];
  clientsViolationErrors: Map<ClientFormType, FieldConstraintViolation[]>;
  onClientsViolationErrorsChange: (clientsViolationErrors: Map<ClientFormType, FieldConstraintViolation[]>) => void;
  setIsFormChanged: VoidFunction;
  policyHolder?: Partial<NaturalClient> | Partial<SelfEmployedClient> | Partial<LegalClient>;
  onCalculationFormSubmit: (calcData: RealtyCalc) => void;
  onClientChange: (
    type: ClientFormType,
    client?: NaturalClient | SelfEmployedClient | LegalClient,
  ) => RealtyFormClients;
  onResetCalculatorClick: (onSuccess: () => void) => void;
  goToCalcResults?: VoidFunction;
}

export const RealtyCalcWrapper = ({
  calcData,
  formErrors,
  clientsViolationErrors,
  onClientsViolationErrorsChange,
  setIsFormChanged,
  policyHolder,
  onCalculationFormSubmit,
  onClientChange,
  onResetCalculatorClick,
  goToCalcResults,
}: Props) => {
  useScrollToTopOnLoad();
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const [form] = Form.useForm<RealtyCalc>();
  const buildingType = Form.useWatch(["generalBuildingData", "type"], form);
  const realtyInsuranceEnabled = Form.useWatch(["realtyInsuranceEnabled"], form);
  const householdInsuranceEnabled = Form.useWatch(["householdInsuranceEnabled"], form);

  const clientValidation = useValidateClient();

  useEffect(() => {
    if (calcData) {
      const { realtyInsuranceData, householdInsuranceData, clientsData } = calcData;

      const policyHolder = isDefined(clientsData.policyHolderIndex)
        ? createUpdateContractClientToClient(
            clientsData.clients?.[clientsData.policyHolderIndex] as CreateUpdateContractClient,
          )
        : undefined;

      form.setFieldsValue({
        ...calcData,
        realtyInsuranceEnabled: !!realtyInsuranceData,
        householdInsuranceEnabled: !!householdInsuranceData,
        realtyInsuranceData: realtyInsuranceData
          ? {
              ...realtyInsuranceData,
              nearbyBuildingReinsurances: realtyInsuranceData.nearbyBuildingReinsurances.map((r) => ({
                ...r,
                placeOfGarageSameAsBuilding:
                  r.type === NearbyBuildingType.DETACHED_GARAGE && !isDefined(r.placeOfGarageSameAsBuilding)
                    ? true
                    : r.placeOfGarageSameAsBuilding,
              })),
            }
          : undefined,
        clientsData: {
          ...calcData.clientsData,
          clients: undefined,
          policyHolderIndex: undefined,
          policyHolderIdentifier:
            calcData.clientsData.policyHolderIdentifier ||
            (policyHolder ? resolveClientIdentifier(policyHolder) : undefined),
        },
      });
    } else {
      form.resetFields();
    }
  }, [calcData]);

  useEffect(() => {
    if (formErrors.length) {
      setErrorsToForm(form, "calc.realty.attrs", formErrors);
    }
  }, [formErrors]);

  const handleFormFieldsChange = (changedFields?: FieldData[]) => {
    if (changedFields?.length) {
      setIsFormChanged();
    }
  };

  const handleCalculationFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (!policyHolder || clientsViolationErrors.has(ClientFormType.POLICY_HOLDER)) {
          notification.error({
            title: t("common.error"),
            description: t("common.validations.formError"),
          });
        } else {
          onCalculationFormSubmit(processAndGetCalcFormData(values));
        }
      })
      .catch((errorInfo) => {
        notification.error({ title: t("common.error"), description: t("common.validations.formError") });
        resolveFormValidationError(errorInfo);
      });
  };

  const handlePolicyHolderChange = (client?: Client) => {
    if (!isDeepEqual(policyHolder, client)) {
      setIsFormChanged();
    }

    onClientChange(ClientFormType.POLICY_HOLDER, client);

    if (client) {
      clientValidation.mutate(
        {
          prefix: `clientsData.clients[0]`,
          client: clientToCreateUpdateContractClient(client),
          validationGroup: CalcValidationGroup.CALCULATE,
        },
        {
          onError: (error) => {
            if (error.violations) {
              onClientsViolationErrorsChange(
                processClientsDataViolations(POLICY_HOLDER_INDEX_MAP, "clientsData.clients", error?.violations),
              );
            }
          },
        },
      );

      if (client.type !== ClientType.NATURAL) {
        form.setFieldsValue({
          householdInsuranceEnabled: false,
          generalInsuranceData: {
            crossSelling: {
              unionHealthContract: false,
            },
          },
        });
      }
    }
  };

  const handlePolicyHolderViolationErrorsDelete = () => {
    onClientsViolationErrorsChange(new Map<ClientFormType, FieldConstraintViolation[]>());
  };

  const processAndGetCalcFormData = (calcData?: RealtyCalc): RealtyCalc => {
    const processedValues = calcData ? { ...calcData } : (form.getFieldsValue() as RealtyCalc);
    processedValues.type = CalcType.REALTY;

    delete processedValues.realtyInsuranceEnabled;
    if (processedValues.realtyInsuranceData && !processedValues.realtyInsuranceData.nearbyBuildingReinsurances) {
      processedValues.realtyInsuranceData.nearbyBuildingReinsurances = [];
    }

    delete processedValues.householdInsuranceEnabled;
    if (processedValues.householdInsuranceData && !processedValues.householdInsuranceData.equipmentReinsurances) {
      processedValues.householdInsuranceData.equipmentReinsurances = [];
    }

    processedValues.clientsData = {
      ...processedValues.clientsData,
      clients: policyHolder ? [clientToCreateUpdateContractClient(policyHolder)] : [],
      policyHolderIndex: POLICY_HOLDER_INDEX_MAP.get(ClientFormType.POLICY_HOLDER) as number,
      policyHolderIdentifier: undefined,
    };

    return processedValues;
  };

  const renderBuildingTypeSection = () => {
    if (buildingType === BuildingType.APARTMENT) {
      return <RealtyCalcApartmentSection form={form} />;
    }

    return <RealtyCalcHouseSection />;
  };

  return (
    <>
      <Form form={form} layout="vertical" name="realtyCalcForm" onFieldsChange={handleFormFieldsChange}>
        <Card
          className="card-box card-box--inner-extra"
          title={<h2>{realtyCalcPageTitle(buildingType, realtyInsuranceEnabled, householdInsuranceEnabled)}</h2>}
          extra={
            <>
              <CalcNoticesIconView position={DashboardNoticePosition.REALTY_CALC} />
              <Link to={CALC_ROUTE_PATHS.realtyDraft.to}>
                <SecondaryButton icon={<AntIcon type="form" />}>{t("calc.actions.showDrafts")}</SecondaryButton>
              </Link>
            </>
          }
        >
          <Row gutter={rowGutter} justify="center">
            <Col span={12} className="center-align">
              <Form.Item name={["generalBuildingData", "type"]} rules={[validations.notNull]}>
                <Radio.Group size="large" buttonStyle="solid" block optionType="button">
                  {Object.values(BuildingType).map((type) => (
                    <Radio.Button
                      value={type}
                      key={type}
                      style={{ height: 60, display: "flex", justifyContent: "center" }}
                    >
                      <AntIcon size="huge" type={resolveBuildingTypeIcon(type)} />
                      <div style={{ fontSize: 16, lineHeight: "8px" }}>
                        {t("calc.realty.enums.buildingType." + type)}
                      </div>
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <RealtyCalcGeneralSection
          form={form}
          policyHolder={policyHolder}
          clientsViolationErrors={clientsViolationErrors}
          onPolicyHolderChange={handlePolicyHolderChange}
          onPolicyHolderViolationErrorsDelete={handlePolicyHolderViolationErrorsDelete}
        />

        {renderBuildingTypeSection()}

        <RealtyCalcHouseholdRealtySection form={form} policyHolderType={policyHolder?.type} />

        <RealtyCalcInsuranceSection policyHolderType={policyHolder?.type} />
      </Form>

      <CalcNavigation
        onCalculate={handleCalculationFormSubmit}
        onResetCalculatorClick={() => onResetCalculatorClick(() => form.resetFields())}
        goToCalcResults={goToCalcResults}
      />
    </>
  );
};
